import React, { useMemo, useState } from "react";
import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Alert,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { SubmissionReviewActivity, ParticipantTeam } from "../../types";
import {
  SET_MANUAL_REVIEW_MAP,
  GET_SUBMISSION_REVIEW_ACTIVITY,
} from "../../Queries";

/** A selectable reviewer: a single student or a whole team (FL-336). A
 * team selection expands to one review per member at allocation time. */
interface ReviewerOption {
  kind: "team" | "student";
  /** "team:<id>" / "user:<id>" — unique across both kinds */
  key: string;
  id: string;
  name: string;
  memberIds: string[];
}

interface SubjectRow {
  /** group id (team mode) or user id (individual mode) */
  key: string;
  name: string;
  /** member user ids — excluded from this subject's reviewer options */
  memberIds: string[];
  memberNames: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  activity: SubmissionReviewActivity;
  onSaved?: () => void;
  /** FL-284: once reviews open the map is locked — view without editing. */
  readOnly?: boolean;
}

/**
 * Full-screen editor for the "Manually selected" reviewer mode: one row per
 * subject (team or student), each with a multi-select of the students who
 * will review it. Saved as explicit reviewer→subject pairings.
 */
export function ManualMappingDialog({
  open,
  onClose,
  activity,
  onSaved,
  readOnly = false,
}: Props) {
  const [t] = useTranslation();
  const isTeamMode = activity.submitterMode === "TM";
  const teams: ParticipantTeam[] = useMemo(
    () => activity.participantsPreview ?? [],
    [activity.participantsPreview],
  );

  const subjects: SubjectRow[] = useMemo(() => {
    if (isTeamMode) {
      return teams.map((team) => ({
        key: team.id,
        name: team.name,
        memberIds: team.members.map((m) => m.id),
        memberNames: team.members.map((m) => m.name),
      }));
    }
    // Individuals: the preview is one flat pseudo-team of all students
    return teams
      .flatMap((team) => team.members)
      .map((member) => ({
        key: member.id,
        name: member.name,
        memberIds: [member.id],
        memberNames: [],
      }));
  }, [isTeamMode, teams]);

  const reviewerPool: ReviewerOption[] = useMemo(() => {
    const seen = new Map<string, ReviewerOption>();
    // FL-336: in team mode whole teams are offered first — picking one
    // assigns every member to review the subject.
    if (isTeamMode) {
      for (const team of teams) {
        seen.set(`team:${team.id}`, {
          kind: "team",
          key: `team:${team.id}`,
          id: team.id,
          name: team.name,
          memberIds: team.members.map((m) => m.id),
        });
      }
    }
    for (const team of teams) {
      for (const member of team.members) {
        seen.set(`user:${member.id}`, {
          kind: "student",
          key: `user:${member.id}`,
          id: member.id,
          name: member.name,
          memberIds: [member.id],
        });
      }
    }
    return [...seen.values()].sort((a, b) =>
      a.kind === b.kind
        ? a.name.localeCompare(b.name)
        : a.kind === "team"
          ? -1
          : 1,
    );
  }, [isTeamMode, teams]);

  const reviewerByKey = useMemo(
    () => new Map(reviewerPool.map((option) => [option.key, option])),
    [reviewerPool],
  );

  // subject key -> reviewer option keys, seeded from the saved pairings
  const [assignments, setAssignments] = useState<Record<string, string[]>>(
    () => {
      const initial: Record<string, string[]> = {};
      for (const pair of activity.manualPairs ?? []) {
        const key = pair.subjectGroupId ?? pair.subjectUserId;
        if (!key) continue;
        const reviewerKey = pair.reviewerGroupId
          ? `team:${pair.reviewerGroupId}`
          : `user:${pair.reviewerId}`;
        initial[key] = [...(initial[key] ?? []), reviewerKey];
      }
      return initial;
    },
  );

  const [setMap, { loading: saving }] = useMutation(SET_MANUAL_REVIEW_MAP, {
    refetchQueries: [
      {
        query: GET_SUBMISSION_REVIEW_ACTIVITY,
        variables: { pk: activity.id },
      },
    ],
    onCompleted: () => {
      onSaved?.();
      onClose();
    },
  });

  const handleSave = () => {
    const pairs = subjects.flatMap((subject) =>
      (assignments[subject.key] ?? []).flatMap((reviewerKey) => {
        const option = reviewerByKey.get(reviewerKey);
        // A seeded key whose team/student left the roster since the pairs
        // were saved has no option — drop it instead of posting a
        // malformed id the backend would bounce.
        if (!option) return [];
        const reviewer =
          option.kind === "team"
            ? { reviewerGroupId: option.id }
            : { reviewerId: option.id };
        return [
          isTeamMode
            ? { ...reviewer, subjectGroupId: subject.key }
            : { ...reviewer, subjectUserId: subject.key },
        ];
      }),
    );
    setMap({ variables: { activityId: activity.id, pairs } });
  };

  const uncoveredSubjects = subjects.filter(
    (subject) => (assignments[subject.key] ?? []).length === 0,
  );
  // A student counts as busy when picked directly OR through their team
  const assignedMemberIds = new Set(
    Object.values(assignments)
      .flat()
      .flatMap((key) => reviewerByKey.get(key)?.memberIds ?? []),
  );
  const idleReviewers = reviewerPool.filter(
    (reviewer) =>
      reviewer.kind === "student" && !assignedMemberIds.has(reviewer.id),
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label={String(t("Close"))}
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {t("Reviewer Mapping")}
          </Typography>
          {!readOnly && (
            <Button
              color="inherit"
              variant="outlined"
              onClick={handleSave}
              disabled={saving}
              startIcon={<Save />}
            >
              {t("Save Mapping")}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 1100, mx: "auto", width: "100%" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {readOnly
            ? t(
                "Reviews are open — the mapping is locked. This is the pairing reviews were created from.",
              )
            : t(
                isTeamMode
                  ? "For each team being reviewed, pick the students — or entire teams — who will review it. Picking a team assigns every one of its members. Students from the reviewed team itself aren't offered."
                  : "For each student being reviewed, pick the students who will review their work.",
              )}
        </Typography>

        {(uncoveredSubjects.length > 0 || idleReviewers.length > 0) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {uncoveredSubjects.length > 0 && (
              <span>
                {uncoveredSubjects.length}{" "}
                {t(
                  isTeamMode
                    ? "team(s) have no reviewers yet."
                    : "student(s) have no reviewers yet.",
                )}{" "}
              </span>
            )}
            {idleReviewers.length > 0 && (
              <span>
                {idleReviewers.length}{" "}
                {t("student(s) are not assigned to review anyone:")}{" "}
                {idleReviewers.map((r) => r.name).join(", ")}
              </span>
            )}
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "35%" }}>
                {t(
                  isTeamMode ? "Team being reviewed" : "Student being reviewed",
                )}
              </TableCell>
              <TableCell>{t("Reviewed by")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.map((subject) => {
              const options = reviewerPool.filter((reviewer) =>
                reviewer.kind === "team"
                  ? reviewer.id !== subject.key
                  : !subject.memberIds.includes(reviewer.id),
              );
              const value = (assignments[subject.key] ?? [])
                .map((key) => reviewerByKey.get(key))
                .filter(Boolean) as ReviewerOption[];
              return (
                <TableRow key={subject.key}>
                  <TableCell sx={{ verticalAlign: "top", pt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {subject.name}
                    </Typography>
                    {subject.memberNames.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {subject.memberNames.join(", ")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      size="small"
                      disabled={readOnly}
                      options={options}
                      groupBy={
                        isTeamMode
                          ? (option) =>
                              option.kind === "team"
                                ? String(t("Teams"))
                                : String(t("Students"))
                          : undefined
                      }
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(a, b) => a.key === b.key}
                      value={value}
                      onChange={(_, selected) =>
                        setAssignments((prev) => ({
                          ...prev,
                          [subject.key]: selected.map((s) => s.key),
                        }))
                      }
                      renderTags={(selected, getTagProps) =>
                        selected.map((option, index) => {
                          const { key: _key, ...tagProps } = getTagProps({
                            index,
                          });
                          return (
                            <Chip
                              key={option.key}
                              label={option.name}
                              size="small"
                              color={
                                option.kind === "team" ? "primary" : undefined
                              }
                              variant={
                                option.kind === "team" ? "outlined" : "filled"
                              }
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            value.length === 0
                              ? String(t("Add reviewers..."))
                              : undefined
                          }
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Dialog>
  );
}
